import { useEffect, useRef, useState } from "react";
import {
  Activity,
  CheckCircle2,
  Database,
  Bot,
  Globe,
  Server,
  AlertTriangle,
  XCircle,
  RefreshCw,
} from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";

/**
 * Public /status page. Polls /api/status every 15s and renders the overall
 * health snapshot plus per-component + per-shard detail cards. Designed to
 * fail deterministically — if the request errors, the page shows the site
 * as "down" rather than a blank screen, matching what any real status page
 * should do.
 */
const REFRESH_MS = 15_000;

const OVERALL_META = {
  operational: {
    label: "All Systems Operational",
    tone: "emerald",
    icon: CheckCircle2,
  },
  degraded: {
    label: "Partial Outage — Some Systems Degraded",
    tone: "amber",
    icon: AlertTriangle,
  },
  down: {
    label: "Major Outage",
    tone: "red",
    icon: XCircle,
  },
  unknown: {
    label: "Checking status…",
    tone: "zinc",
    icon: Activity,
  },
};

export default function Status() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);
  const timerRef = useRef(null);

  const fetchStatus = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/status");
      setData(data);
      setError(false);
    } catch {
      setError(true);
      setData(null);
    } finally {
      setLastFetch(new Date());
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    timerRef.current = setInterval(fetchStatus, REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, []);

  const overallKey = error ? "down" : data?.overall || "unknown";
  const overall = OVERALL_META[overallKey];
  const Icon = overall.icon;
  const c = data?.components || {};

  return (
    <div
      className="pt-24 pb-16 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8"
      data-testid="status-page"
    >
      <SEO
        title="Status — Serverlistr uptime, bot & database health"
        description="Live status of Serverlistr — website, API, Discord bot, and database. Auto-refreshing every 15 seconds."
        path="/status"
      />
      <Breadcrumbs items={[{ name: "Status" }]} />

      {/* Overall banner */}
      <div
        className={`rounded-2xl border p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-4 ${banner(
          overall.tone
        )}`}
        data-testid="status-overall"
      >
        <div
          className={`w-12 h-12 rounded-xl flex items-center justify-center ${iconWrap(
            overall.tone
          )}`}
        >
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">
            {overall.label}
          </h1>
          <p className="mt-1 text-xs text-zinc-500">
            {lastFetch
              ? `Last checked ${lastFetch.toLocaleTimeString()} · auto-refresh every 15s`
              : "Fetching…"}
          </p>
        </div>
        <button
          onClick={fetchStatus}
          disabled={refreshing}
          className="h-10 px-4 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-semibold inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="status-refresh-btn"
        >
          <RefreshCw
            className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`}
          />
          Refresh
        </button>
      </div>

      {/* Component cards */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ComponentCard
          icon={<Globe className="w-5 h-5" />}
          name="Website"
          status={c.website?.status}
          detail="Frontend served from serverlistr.net"
          testid="status-website"
        />
        <ComponentCard
          icon={<Server className="w-5 h-5" />}
          name="API"
          status={c.api?.status}
          detail={
            error
              ? "Unable to reach /api/status"
              : "REST endpoints powering the site"
          }
          testid="status-api"
        />
        <ComponentCard
          icon={<Database className="w-5 h-5" />}
          name="Database"
          status={c.database?.status}
          detail={
            c.database?.ping_ms != null
              ? `MongoDB ping: ${c.database.ping_ms}ms`
              : "MongoDB primary"
          }
          testid="status-database"
        />
        <ComponentCard
          icon={<Bot className="w-5 h-5" />}
          name="Discord Bot"
          status={
            c.discord_bot?.connected
              ? "operational"
              : c.discord_bot?.state === "connecting"
              ? "degraded"
              : "down"
          }
          detail={
            c.discord_bot?.connected
              ? `${(c.discord_bot?.guilds || 0).toLocaleString()} guilds · ${c.discord_bot?.latency_ms ?? "—"}ms gateway`
              : c.discord_bot?.state === "connecting"
              ? "Connecting to Discord gateway…"
              : "Gateway offline"
          }
          testid="status-bot"
        />
      </div>

      {/* Discord bot detail */}
      {c.discord_bot && (
        <section className="mt-10" data-testid="status-bot-detail">
          <div className="flex items-baseline justify-between gap-3 mb-4">
            <h2 className="font-heading text-xl sm:text-2xl font-black">
              Discord Bot Detail
            </h2>
            {c.discord_bot.user && (
              <span className="text-xs text-zinc-500 font-mono">
                {c.discord_bot.user}
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCell
              label="Status"
              value={
                c.discord_bot.connected ? "Ready" : c.discord_bot.state || "—"
              }
              accent={c.discord_bot.connected ? "emerald" : "amber"}
            />
            <StatCell
              label="Gateway latency"
              value={
                c.discord_bot.latency_ms != null
                  ? `${c.discord_bot.latency_ms} ms`
                  : "—"
              }
            />
            <StatCell
              label="Guilds"
              value={(c.discord_bot.guilds || 0).toLocaleString()}
            />
            <StatCell
              label="Uptime"
              value={formatUptime(c.discord_bot.uptime_seconds)}
            />
          </div>

          {/* Shard table */}
          <div className="mt-6 rounded-2xl border border-white/10 bg-[#0A0A0F] overflow-hidden">
            <div className="grid grid-cols-4 px-4 py-3 text-[10px] uppercase tracking-wider text-zinc-500 font-bold border-b border-white/10">
              <div>Shard</div>
              <div>Status</div>
              <div>Latency</div>
              <div className="text-right">Guilds</div>
            </div>
            {(c.discord_bot.shards || []).map((s) => (
              <div
                key={s.id}
                className="grid grid-cols-4 px-4 py-3 text-sm border-b border-white/5 last:border-b-0 tabular-nums"
                data-testid={`status-shard-${s.id}`}
              >
                <div className="font-mono text-zinc-300">#{s.id}</div>
                <div>
                  <span
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold ${
                      s.status === "ready"
                        ? "text-emerald-300"
                        : "text-amber-300"
                    }`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        s.status === "ready" ? "bg-emerald-400" : "bg-amber-400"
                      } animate-pulse`}
                    />
                    {s.status}
                  </span>
                </div>
                <div className="text-zinc-300">
                  {s.latency_ms != null ? `${s.latency_ms} ms` : "—"}
                </div>
                <div className="text-zinc-300 text-right">
                  {(s.guilds || 0).toLocaleString()}
                </div>
              </div>
            ))}
            {(!c.discord_bot.shards || c.discord_bot.shards.length === 0) && (
              <div className="px-4 py-6 text-center text-sm text-zinc-500">
                No shard data — bot is offline.
              </div>
            )}
          </div>

          <p className="mt-4 text-xs text-zinc-600">
            Process uptime: {formatUptime(c.discord_bot.process_uptime_seconds)}
            {" · "}
            <a
              href="/api/status"
              target="_blank"
              rel="noreferrer"
              className="hover:text-zinc-300 underline underline-offset-2"
            >
              Raw JSON
            </a>
          </p>
        </section>
      )}
    </div>
  );
}

// -------- helpers ---------------------------------------------------

function ComponentCard({ icon, name, status, detail, testid }) {
  const s = status || "unknown";
  const label =
    s === "operational" ? "Operational" : s === "degraded" ? "Degraded" : s === "down" ? "Down" : "Unknown";
  const dot =
    s === "operational"
      ? "bg-emerald-400"
      : s === "degraded"
      ? "bg-amber-400"
      : "bg-red-400";
  const text =
    s === "operational"
      ? "text-emerald-300"
      : s === "degraded"
      ? "text-amber-300"
      : "text-red-300";

  return (
    <div
      className="rounded-2xl border border-white/10 bg-[#0A0A0F] p-5"
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-zinc-400 shrink-0">
            {icon}
          </div>
          <div className="min-w-0">
            <div className="font-heading font-bold text-base truncate">
              {name}
            </div>
            <div className="text-xs text-zinc-500 truncate">{detail}</div>
          </div>
        </div>
        <div
          className={`inline-flex items-center gap-1.5 text-xs font-semibold ${text} shrink-0`}
        >
          <span className={`w-2 h-2 rounded-full ${dot} animate-pulse`} />
          {label}
        </div>
      </div>
    </div>
  );
}

function StatCell({ label, value, accent }) {
  const accentText =
    accent === "emerald"
      ? "text-emerald-300"
      : accent === "amber"
      ? "text-amber-300"
      : "text-white";
  return (
    <div className="rounded-xl border border-white/10 bg-[#0A0A0F] px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">
        {label}
      </div>
      <div
        className={`mt-1 font-heading text-xl font-black tabular-nums ${accentText}`}
      >
        {value}
      </div>
    </div>
  );
}

function formatUptime(seconds) {
  const s = Number(seconds) || 0;
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

function banner(tone) {
  return {
    emerald:
      "border-emerald-500/30 bg-gradient-to-r from-emerald-500/10 via-transparent to-emerald-500/5",
    amber:
      "border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-transparent to-amber-500/5",
    red: "border-red-500/30 bg-gradient-to-r from-red-500/10 via-transparent to-red-500/5",
    zinc: "border-white/10 bg-[#0A0A0F]",
  }[tone];
}

function iconWrap(tone) {
  return {
    emerald: "bg-emerald-500/15 border border-emerald-500/40 text-emerald-300",
    amber: "bg-amber-500/15 border border-amber-500/40 text-amber-300",
    red: "bg-red-500/15 border border-red-500/40 text-red-300",
    zinc: "bg-white/5 border border-white/10 text-zinc-400",
  }[tone];
}
